import * as clientErrTypes from 'config/clientErrorTypes';
import { notQualifiedTypes } from 'config/notQualifiedTypes';
import { PromiseAll } from 'api/common';
import { createUserEntries, getUserLatestEntries } from 'api/client';
import { isFulFilledEntries } from 'client/hooks/useInitPromo/userEntryState/entriesChecker';
import checkEntryNeedPoint from 'client/hooks/useInitPromo/userEntryState/checkEntryNeedPoint';
import {
    checkHasReachPlayLimit,
    checkHasReachPeriodPlayLimit
} from 'client/hooks/useHasReachPlayTimesLimit';
import {
    isGetEntriesLimitError,
    isGetEntriesPlayTimeLimitErr,
    isRequestAtInvalidPromoTime,
    isErrOfGetEntryWhenPromoInvalid,
    isErrOfMemberOrderLimit,
    isCreateEntryTotalMonthlyEntryLimitError,
    isErrOfTagLimit
} from 'client/utils/errorObjectChecker';
import { suspendPromo } from 'client/redux/modules/promoState';
import { gameTypes } from 'config/promoGameTypes';
import { generateApiErrObj } from 'client/hooks/useClientErrorHandler';
import checkEntryNeedPasscode from 'client/utils/passcode/checkEntryNeedPasscode';

export async function getUserGameEntry({ dispatch, state, latestEntry, recaptchaToken }) {
    const {
        promo: { uuid: promoUUID },
        lotteryInstant: { uuid: lotteryInstantUUID },
        lotteryParticipant: { uuid: lotteryParticipantUUID },
        signUpForm: { id: signUpFormId, enableFilledByEntry } = {},
        promoState: { isTestMode },
        userGameResultRecords
    } = state;
    const {
        entry,
        isPlayTimeLimitError,
        isNotValidPromoJoinTime,
        isNotValidPromoStatusToJoin,
        createEntryNotQualifiedType
    } = await createNewEntry(promoUUID, recaptchaToken);
    if (isNotValidPromoJoinTime || isPlayTimeLimitError) {
        return { entry: latestEntry };
    } else if (isNotValidPromoStatusToJoin) {
        dispatch(suspendPromo());
        return { entry: overwriteEntryAction(latestEntry), hasPromoSuspended: true };
    } else if (createEntryNotQualifiedType) {
        if (createEntryNotQualifiedType === notQualifiedTypes.ENTRIES.REACH_LIMIT && !isTestMode) {
            dispatch(suspendPromo());
            return {
                entry: overwriteEntryAction(latestEntry),
                createEntryNotQualifiedType,
                hasPromoSuspended: true
            };
        }
        return {
            entry: latestEntry,
            createEntryNotQualifiedType
        };
    } else {
        const hasEnableSignUpForm = signUpFormId !== undefined;
        const hasEnableLotteryInstant = lotteryInstantUUID !== undefined;
        const hasEnableLotteryParticipant = lotteryParticipantUUID !== undefined;
        const gameEntry = normalizeEntryPayload(entry, {
            hasEnableSignUpForm,
            hasEnableLotteryInstant,
            hasEnableLotteryParticipant
        });
        if (!enableFilledByEntry) {
            // if once fill signup form before, and does not set "enableFilledByEntry" then "hasFilledSignupForm" status should be true forever
            // 這行是指當開放多次參加，上一筆 entry 的填表狀態要傳承給下一筆新建立的 entry，前提是表單要開 "同一登入者只能填寫一次"，而非 "每次參加都需要填寫報名表單"
            const previousEntry = userGameResultRecords.find(r => r?.id === latestEntry.entryId);
            // 此entry是剛新建立的entry，且如果上一筆entry被刪除(Ex: 審核未通過、取消資格)，且上一筆entry沒填表，就不繼承上一筆的 entry 填表紀錄，直接判定下一筆沒填過表，需要送去填表頁補填表
            if (previousEntry?.deletedAt && !previousEntry.flow?.formSignUp) {
                gameEntry.hasFilledSignupForm = false;
            } else {
                gameEntry.hasFilledSignupForm = latestEntry.hasFilledSignupForm;
            }
        }

        return { entry: gameEntry };
    }
}
export async function createNewEntry(promoUUID, recaptchaToken) {
    let resp = await createUserEntries(promoUUID, recaptchaToken);
    if (resp.httpStatus !== 200) {
        if (isCreateEntryTotalMonthlyEntryLimitError(resp)) {
            return { entry: null, isTotalMonthlyEntryLimitError: true };
        } else if (isGetEntriesPlayTimeLimitErr(resp)) {
            return { entry: null, isPlayTimeLimitError: true };
        } else if (isRequestAtInvalidPromoTime(resp)) {
            return { entry: null, isNotValidPromoJoinTime: true };
        } else if (isErrOfGetEntryWhenPromoInvalid(resp)) {
            return { entry: null, isNotValidPromoStatusToJoin: true };
        } else {
            const createEntryNotQualifiedType = getCreateEntryNotQualifedType(resp);
            if (createEntryNotQualifiedType) {
                return { entry: null, createEntryNotQualifiedType };
            } else {
                throw generateApiErrObj(resp, clientErrTypes.CREATE_ENTRIES);
            }
        }
    }
    return { entry: resp.payload };
}
function getCreateEntryNotQualifedType(resp) {
    if (isGetEntriesLimitError(resp)) {
        return notQualifiedTypes.ENTRIES.REACH_LIMIT;
    } else if (isErrOfMemberOrderLimit(resp)) {
        return notQualifiedTypes.QUALIFICATION.MEMBER_ORDER;
    } else if (isErrOfTagLimit(resp)) {
        return notQualifiedTypes.QUALIFICATION.TAG_LIMIT;
    }
    return '';
}
export async function getUserLatestEntry({ state }) {
    const {
        promo: { uuid: promoUUID },
        lotteryInstant: { uuid: lotteryInstantUUID },
        lotteryParticipant: { uuid: lotteryParticipantUUID },
        signUpForm: { id: signUpFormId } = {}
    } = state;
    const hasEnableSignUpForm = signUpFormId !== undefined;
    const hasEnableLotteryInstant = lotteryInstantUUID !== undefined;
    const hasEnableLotteryParticipant = lotteryParticipantUUID !== undefined;
    const { payload } = await getUserLatestEntries(promoUUID);
    const entry = normalizeEntryPayload(payload, {
        hasEnableSignUpForm,
        hasEnableLotteryInstant,
        hasEnableLotteryParticipant
    });

    return entry;
}
export function checkShouldGetNextEntry({ state, latestEntry }) {
    const {
        promo: { type: gameType },
        settingPromo: { totalPlayTimes, dailyPlayTimes, timezone },
        promoState: { periodPlayMatchedInfo: { isPeriodPlayMode, matchedInfo = {} } = {} },
        userGameResultRecords: gameResultRecords,
        userLoginState: { notQualifiedType }
    } = state;

    if (gameType === gameTypes.INSTAGRAM) {
        return false;
    }

    const isQualifiedPlayer = !Boolean(notQualifiedType);
    if (isQualifiedPlayer) {
        const hasGotLatestEntry = Boolean(latestEntry.entryUuid);
        if (!hasGotLatestEntry) {
            return true;
        } else if (isFulFilledEntries(gameType, latestEntry)) {
            const hasReachPlayLimit = isPeriodPlayMode
                ? checkHasReachPeriodPlayLimit({
                      gameResultRecords,
                      timezone,
                      totalPlayTimes,
                      matchedInfo
                  })
                : checkHasReachPlayLimit({
                      gameResultRecords,
                      timezone,
                      totalPlayTimes,
                      dailyPlayTimes
                  });
            return !hasReachPlayLimit;
        }
    }
    return false;
}

export function normalizeEntryPayload(
    payload = {},
    {
        hasEnableSignUpForm = false,
        hasEnableLotteryInstant = false,
        hasEnableLotteryParticipant = false
    } = {}
) {
    const {
        id,
        serialNo,
        entryUuid,
        entryFlow: {
            // 已填過表單
            formSignUp = '',
            gameResult = '',
            lotteryInstant = '',
            lotteryParticipant = '',
            partnerOrder,
            qualifyWithdraw,
            // 已填過邀請碼
            mgmReferralCode = ''
        } = {},
        deletedAt
    } = payload;
    const isEntryDeleted = Boolean(deletedAt);
    return {
        entryId: id || serialNo,
        entryUuid,
        gameResultCreatedAt: gameResult,
        hasGameResult: Boolean(gameResult),
        hasSubmitTracking: Boolean(gameResult),
        hasPointReduced: Boolean(partnerOrder || qualifyWithdraw),
        hasReferralCodeInput: Boolean(mgmReferralCode),
        // 所有被刪除的 entry 會被當作可以 略過填表 和 已經配獎，所以會把 hasFilledSignupForm、hasParticipantPrize 直接設定為 true
        // 沒被刪除的 entry 或是新建立的 entry 會根據 entry flow 檢查此 entry 有無填表紀錄(formSignUp) 來設定 hasFilledSignupForm
        hasFilledSignupForm: !isEntryDeleted && hasEnableSignUpForm ? Boolean(formSignUp) : true,
        hasInstantPrize:
            !isEntryDeleted && hasEnableLotteryInstant ? Boolean(lotteryInstant) : true,
        hasParticipantPrize:
            !isEntryDeleted && hasEnableLotteryParticipant ? Boolean(lotteryParticipant) : true
    };
}
export function overwriteEntryAction(entry) {
    // 活動不可玩時，略過填表和配獎
    return { ...entry, hasFilledSignupForm: true, hasParticipantPrize: true };
}
export async function getEntryQualificationInfo({ state, entry = {} }) {
    const {
        promo: { uuid: promoUUID },
        promoState: { reducePointMode, enablePasscodeVerify },
        flags: { isMgmMission, isMgmSelf } = {},
        userGameResultRecords = [],
        settingMGMReferral: { enableReferralCodeRequired } = {}
    } = state;
    const { hasReferralCodeInput } = entry;
    const [isNeedReducePoint, isNeedEnterPasscode] = await PromiseAll([
        checkEntryNeedPoint({
            promoUUID,
            entry: entry,
            reducePointMode
        }),
        checkEntryNeedPasscode({
            promoUUID,
            entryUUID: entry.entryUuid,
            enablePasscodeVerify
        })
    ]);

    // 是任務模組 且 當前未有 GameResult 且 當前 Entry 未填邀請 且 啟用邀請碼必填時，一定要填邀請碼
    const isNeedEnterReferralCode = Boolean(
        (isMgmMission || isMgmSelf) &&
            userGameResultRecords.length === 0 &&
            Boolean(enableReferralCodeRequired) &&
            !hasReferralCodeInput
    );
    // 是任務模組 且 當前未有 GameResult 且 當前 Entry 未填邀請碼時，可填邀請碼
    const isCanEnterReferralCode = Boolean(
        (isMgmMission || isMgmSelf) && userGameResultRecords.length === 0 && !hasReferralCodeInput
    );

    return {
        isNeedReducePoint,
        isNeedEnterPasscode,
        isNeedEnterReferralCode,
        isCanEnterReferralCode
    };
}
